import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  formatSchedulerLocalizedDateString,
  HOURS_PER_DAY,
  localize,
} from '@saviynt/common';
import { Box } from '@saviynt/design-system';
import classnames from 'classnames';
import {
  addDays,
  differenceInHours,
  endOfDay,
  intervalToDuration,
  isEqual,
  isSameDay,
  startOfDay,
  subSeconds,
} from 'date-fns';
import PropTypes from 'prop-types';

import { Session } from '../../models/PamModels';

import SchedulerDay from './ScheduleDay/SchedulerDay';
import { msgs, STATUS } from './constants';
import {
  isLongTermReservation,
  isMultiDayReservation,
  isShortTermReservation,
} from './helpers';

import './Scheduler.css';

function Scheduler({
  sessions,
  numOfDaysDisplayed,
  timeFormat,
  isModalView,
  dataTestId,
  className,
  statusFilter,
  roleFilter,
  showRequestId,
  selectedRoles,
}) {
  const classes = classnames('Scheduler', className);
  const [filteredSessions, setFilteredSessions] = useState(sessions);
  const [selectedRoleKeys, setSelectedRoleKeys] = useState([]);

  const intl = useIntl();

  const SUNDAY = localize(intl, msgs.pam.scheduler.sunday);
  const MONDAY = localize(intl, msgs.pam.scheduler.monday);
  const TUESDAY = localize(intl, msgs.pam.scheduler.tuesday);
  const WEDNESDAY = localize(intl, msgs.pam.scheduler.wednesday);
  const THURSDAY = localize(intl, msgs.pam.scheduler.thursday);
  const FRIDAY = localize(intl, msgs.pam.scheduler.friday);
  const SATURDAY = localize(intl, msgs.pam.scheduler.saturday);

  // Track changes in role filter and status filter and update filtered sessions
  useEffect(() => {
    setSelectedRoleKeys(roleFilter || []);

    // If no role filter is applied, show all sessions
    if (!roleFilter || roleFilter.length === 0) {
      setFilteredSessions(sessions);
    } else {
      // Filter sessions based on roleKey
      const sessionsFilteredByRole = sessions.filter(
        (session) => session.roleKey && roleFilter.includes(session.roleKey)
      );

      setFilteredSessions(sessionsFilteredByRole);
    }
  }, [roleFilter, statusFilter, sessions]);

  const isFiltered = (reservations) => {
    const isNoFilters = statusFilter.length === 0;
    const isRoleFilterApplied = roleFilter?.length > 0;

    const isAvailable = statusFilter.includes(STATUS.AVAILABLE);

    const isAvailableOnly =
      statusFilter.every((filter) => filter === STATUS.AVAILABLE) &&
      reservations.length === 0;

    const isUnavailableOnly =
      statusFilter.every((filter) => filter === STATUS.UNAVAILABLE) &&
      reservations.length > 0;

    // Check if any reservation matches the role filter
    const hasMatchingRoleReservation =
      isRoleFilterApplied &&
      reservations.some(
        (reservation) =>
          reservation.roleKey && selectedRoleKeys.includes(reservation.roleKey)
      );

    const shouldShowAsAvailable = isNoFilters || isAvailable || isAvailableOnly;

    // If no role filter is applied, use status filter logic
    if (!isRoleFilterApplied) {
      return isNoFilters || isAvailable || isAvailableOnly || isUnavailableOnly;
    }

    return hasMatchingRoleReservation || shouldShowAsAvailable;
  };

  const getMutatedSessionsWithMultiDayRes = (initialSessions) => {
    const session = initialSessions.filter(
      (initialSession) => !isShortTermReservation(initialSession)
    );

    return session.map((filteredSession) =>
      isLongTermReservation(filteredSession)
        ? {
            ...filteredSession,
            sessionEndDate: endOfDay(
              new Date(filteredSession.sessionStartDate)
            ).toString(),
          }
        : filteredSession
    );
  };

  const renderBlock = () => {
    const weekdays = [
      SUNDAY,
      MONDAY,
      TUESDAY,
      WEDNESDAY,
      THURSDAY,
      FRIDAY,
      SATURDAY,
    ];
    const daysArray = [];

    for (let i = 0; i < numOfDaysDisplayed; i += 1) {
      const currentDate = new Date();

      currentDate.setDate(currentDate.getDate() + i);
      daysArray.push(currentDate);
    }

    // ASSIGN SESSIONS BY DAY
    // TODO: Optimize nested loops
    return daysArray.map((day) => {
      const reservedSessions = [];
      const multiDayResSessionsByDay = [];

      // Parse sessions for multi-day reservations
      filteredSessions.forEach((session) => {
        const sessionStartDate = new Date(session.sessionStartDate);
        let sessionEndDate = new Date(session.sessionEndDate);

        // Check if session end time is exactly at the start of the day (midnight)
        if (isEqual(sessionEndDate, startOfDay(sessionEndDate))) {
          // Subtract one second if the time is exactly midnight
          sessionEndDate = subSeconds(sessionEndDate, 1);
        }

        // Separate each multi-day reservation
        if (isMultiDayReservation(session)) {
          let durationOfMultiDayRes = intervalToDuration({
            start: sessionStartDate,
            end: sessionEndDate,
          });

          const isSessionDurationWithinOneDay =
            differenceInHours(sessionEndDate, startOfDay(day)) <
              HOURS_PER_DAY ||
            differenceInHours(sessionEndDate, sessionStartDate) < HOURS_PER_DAY;

          // Short-term Reservation (=<24hrs)
          if (isSessionDurationWithinOneDay) {
            let shortTermResByDay;

            if (
              differenceInHours(sessionEndDate, sessionStartDate) >
              HOURS_PER_DAY
            ) {
              let longTermResSessionStartDate = sessionStartDate;

              const longTermResSessionEndDate = sessionEndDate;

              longTermResSessionStartDate = addDays(
                longTermResSessionStartDate,
                durationOfMultiDayRes.days
              );

              const longTermResSessionStartDateNeutral = startOfDay(
                longTermResSessionStartDate
              );

              durationOfMultiDayRes = intervalToDuration({
                start: longTermResSessionStartDateNeutral,
                end: longTermResSessionEndDate,
              });
            }

            const { days: reservationDays, hours: reservationHours } =
              durationOfMultiDayRes;

            const numOfReservedDays =
              reservationDays <= 0 && reservationHours > 0
                ? reservationDays + 1
                : reservationDays;

            for (let i = 0; i < numOfReservedDays + 1; i += 1) {
              const startDate = sessionStartDate;
              const endDate = sessionEndDate;

              if (i === 0) {
                shortTermResByDay = {
                  ...session,
                  sessionStartDate: startDate.toString(),
                  sessionEndDate: endOfDay(startDate).toString(),
                };
              } else {
                shortTermResByDay = {
                  ...session,
                  sessionStartDate: startOfDay(endDate).toString(),
                  sessionEndDate: endDate.toString(),
                };
              }

              multiDayResSessionsByDay.push(shortTermResByDay);
            }
          } else {
            const numOfReservedDays = durationOfMultiDayRes.days + 1;

            // Long-term reservation (>24hrs)
            for (let i = 0; i < numOfReservedDays; i += 1) {
              let longTermResByDay;

              let longTermResSessionStartDate = sessionStartDate;

              const longTermResSessionEndDate = sessionEndDate;

              longTermResSessionStartDate = addDays(
                longTermResSessionStartDate,
                i + 1
              );

              const longTermResSessionStartDateNeutral = startOfDay(
                longTermResSessionStartDate
              );

              if (
                isSameDay(
                  longTermResSessionStartDate,
                  longTermResSessionEndDate
                )
              ) {
                longTermResByDay = {
                  ...session,
                  sessionStartDate:
                    longTermResSessionStartDateNeutral.toString(),
                };
              } else {
                longTermResByDay = {
                  ...session,
                  sessionStartDate:
                    longTermResSessionStartDateNeutral.toString(),
                  sessionEndDate: sessionEndDate.toString(),
                };
              }

              multiDayResSessionsByDay.push(longTermResByDay);
            }
          }
        }
      });

      // mutate/filter out original multi-day reservations
      const mutatedSessions =
        getMutatedSessionsWithMultiDayRes(filteredSessions);

      // Mutate the original sessions array to include the newly daily-divided long-term reservations
      const mutatedSessionsArray = Array.prototype.concat(
        mutatedSessions,
        multiDayResSessionsByDay
      );

      // Traverse the final mutated sessions array and assign the session(s) to each scheduler day by start date
      mutatedSessionsArray.forEach((session) => {
        const sessionStartDate = new Date(session.sessionStartDate);
        const currentDay = new Date(day);

        if (isSameDay(sessionStartDate, currentDay)) {
          reservedSessions.push(session);
        }
      });

      // Create availableStartDate based on role filter and status filter
      const availableStartDate = new Date(day);

      // Default to midnight (start of day)
      availableStartDate.setHours(0, 0, 0, 0);

      // Check if filters are applied
      const isUnavailableFilterApplied = statusFilter.includes(
        STATUS.UNAVAILABLE
      );
      const isAvailableFilterApplied = statusFilter.includes(STATUS.AVAILABLE);
      const isRoleFilterApplied = selectedRoleKeys.length > 0;

      const allDaySessions = sessions.filter((session) => {
        const sessionStartDate = new Date(session.sessionStartDate);
        const currentDay = new Date(day);

        return isSameDay(sessionStartDate, currentDay);
      });

      if (isRoleFilterApplied) {
        const sessionsToCheck = isUnavailableFilterApplied
          ? allDaySessions
          : reservedSessions;

        const roleSpecificSessions = sessionsToCheck.filter(
          (session) =>
            session.roleKey && selectedRoleKeys.includes(session.roleKey)
        );

        if (roleSpecificSessions.length > 0) {
          // Sort sessions by start time to find the earliest one
          roleSpecificSessions.sort(
            (a, b) =>
              new Date(a.sessionStartDate) - new Date(b.sessionStartDate)
          );

          // Use the start time of the earliest session for this role
          const earliestSession = roleSpecificSessions[0];
          const earliestStartDate = new Date(earliestSession.sessionStartDate);

          // Only update the time portion, keep the same day
          availableStartDate.setHours(
            earliestStartDate.getHours(),
            earliestStartDate.getMinutes(),
            0,
            0
          );
        }
      } else if (statusFilter.length > 0 && !isAvailableFilterApplied) {
        const sessionsToSort = isUnavailableFilterApplied
          ? allDaySessions
          : reservedSessions;

        if (sessionsToSort.length > 0) {
          const sortedSessions = [...sessionsToSort].sort(
            (a, b) =>
              new Date(a.sessionStartDate) - new Date(b.sessionStartDate)
          );

          if (sortedSessions.length > 0) {
            const earliestStartDate = new Date(
              sortedSessions[0].sessionStartDate
            );

            availableStartDate.setHours(
              earliestStartDate.getHours(),
              earliestStartDate.getMinutes(),
              0,
              0
            );
          }
        }
      }

      const isToday = daysArray.indexOf(day) === 0;

      return (
        <Box
          key={daysArray.indexOf(day)}
          className={
            isFiltered(reservedSessions)
              ? 'Scheduler-block'
              : 'Scheduler-block--hidden'
          }>
          {isFiltered(reservedSessions) && (
            <section className='Scheduler-blockLabel'>
              <div className='Scheduler-blockLabel--weekday'>
                {`${isToday ? 'Today' : weekdays[day.getDay()]}, `}
              </div>
              <div className='Scheduler-blockLabel--date'>
                {formatSchedulerLocalizedDateString(day)}
              </div>
            </section>
          )}
          <SchedulerDay
            sessions={reservedSessions}
            timeFormat={timeFormat}
            isModalView={isModalView}
            availableStartDate={availableStartDate}
            statusFilter={statusFilter}
            currentDate={day}
            showRequestId={showRequestId}
            selectedRoles={selectedRoles}
          />
        </Box>
      );
    });
  };

  return (
    <section className={classes} data-testid={dataTestId}>
      {renderBlock()}
    </section>
  );
}

Scheduler.propTypes = {
  sessions: PropTypes.arrayOf(Session).isRequired,
  numOfDaysDisplayed: PropTypes.number,
  timeFormat: PropTypes.shape({
    roundToNearest: PropTypes.string,
    is24HourFormat: PropTypes.bool,
  }).isRequired,
  isModalView: PropTypes.bool,
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  statusFilter: PropTypes.arrayOf(PropTypes.string),
  roleFilter: PropTypes.arrayOf(PropTypes.number),
  showRequestId: PropTypes.bool,
  selectedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleName: PropTypes.string,
      roleKey: PropTypes.number,
      roleDisplayName: PropTypes.string,
      roleDescription: PropTypes.string,
      platformType: PropTypes.string,
    })
  ).isRequired,
};

Scheduler.defaultProps = {
  numOfDaysDisplayed: 10,
  isModalView: false,
  dataTestId: null,
  className: null,
  statusFilter: [],
  roleFilter: [],
  showRequestId: true,
};

export default Scheduler;
